import React, { useState } from "react";
import styled from "styled-components";
import { getFunctions, httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import { Button, TextInput } from "../../../components/standardElements";
import ConfirmModal from "../../../components/ConfirmModal";
import { COLORS } from "../../../helpers/designConstants";
import { useAuth } from "../../../database/useAuth";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin: 0 0 16px 0;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid ${COLORS.border};
  border-radius: 6px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserEmail = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.textPrimary};
`;

const UserRole = styled.span`
  font-size: 12px;
  color: ${COLORS.textSecondary};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${props => props.isSuper ? '#ecfdf5' : '#f3f4f6'};
  color: ${props => props.isSuper ? '#059669' : '#6b7280'};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
`;

const Form = styled.form`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.textPrimary};
  margin-bottom: 6px;
`;

const StyledButton = styled(Button)`
  padding: 8px 16px;
  font-size: 14px;
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  color: ${COLORS.error};
  font-size: 14px;
  margin-top: 8px;
`;

const InfoMessage = styled.div`
  background: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  padding: 12px 16px;
  color: #2563eb;
  font-size: 14px;
  line-height: 1.5;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${COLORS.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${COLORS.textSecondary};
`;

const UserManagement = ({ users: propUsers = [] }) => {
  const { user, refreshCustomClaims } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Use prop users directly instead of local state
  const users = propUsers;
  const isInitialized = users.length > 0;

  const functions = getFunctions();


  // Initialize first superAdmin
  const initializeSuperAdmin = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const initializeSuperAdmin = httpsCallable(functions, "admin-initializeSuperAdmin");
      const result = await initializeSuperAdmin();
      
      toast.success("SuperAdmin initialized successfully!");
      
      // Refresh custom claims for current user
      await refreshCustomClaims();
      
      // Reload users
      // Users will be refreshed via parent component
    } catch (err) {
      console.error("Error initializing superAdmin:", err);
      setError(err.message || "Failed to initialize superAdmin");
      toast.error("Failed to initialize superAdmin");
    } finally {
      setIsLoading(false);
    }
  };

  // Grant or revoke superAdmin
  const updateUserClaims = async (targetEmail, grantSuperAdmin) => {
    try {
      setIsLoading(true);
      setError(null);
      setShowConfirmModal(false);

      const setUserClaims = httpsCallable(functions, "admin-setUserClaims");
      const result = await setUserClaims({
        email: targetEmail,
        customClaims: { superAdmin: grantSuperAdmin }
      });
      
      toast.success(`SuperAdmin ${grantSuperAdmin ? 'granted to' : 'revoked from'} ${targetEmail}`);
      
      // Reload users
      // Users will be refreshed via parent component
      
      // If user modified their own role, refresh claims
      if (targetEmail === user.email) {
        await refreshCustomClaims();
      }
    } catch (err) {
      console.error("Error updating user claims:", err);
      setError(err.message || "Failed to update user claims");
      toast.error("Failed to update user claims");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleGrantAccess = (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setConfirmAction({
      type: 'grant',
      email: email.trim(),
      message: `Are you sure you want to grant superAdmin access to ${email.trim()}?`
    });
    setShowConfirmModal(true);
  };

  // Handle revoke access
  const handleRevokeAccess = (userEmail) => {
    setConfirmAction({
      type: 'revoke',
      email: userEmail,
      message: `Are you sure you want to revoke superAdmin access from ${userEmail}?`
    });
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (confirmAction.type === 'grant') {
      updateUserClaims(confirmAction.email, true);
      setEmail("");
    } else {
      updateUserClaims(confirmAction.email, false);
    }
  };

  // Users data now comes from props - no need to fetch

  if (isLoading && users.length === 0) {
    return <LoadingState>Loading user management...</LoadingState>;
  }

  // Show initialization screen if no superAdmin exists
  if (!isInitialized && !user?.isSuperAdmin) {
    return (
      <Container>
        <Section>
          <SectionTitle>Initialize Admin System</SectionTitle>
          <InfoMessage>
            No superAdmin users exist yet. Click the button below to make yourself the first superAdmin.
            This action can only be performed once.
          </InfoMessage>
          <div style={{ marginTop: '16px' }}>
            <StyledButton 
              onClick={initializeSuperAdmin}
              disabled={isLoading}
              style={{ backgroundColor: COLORS.primary }}
            >
              Initialize as SuperAdmin
            </StyledButton>
          </div>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Section>
      </Container>
    );
  }

  return (
    <Container>
      <Section>
        <SectionTitle>Grant SuperAdmin Access</SectionTitle>
        <Form onSubmit={handleGrantAccess}>
          <InputWrapper>
            <Label>User Email</Label>
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </InputWrapper>
          <StyledButton
            type="submit"
            disabled={isLoading || !email.trim()}
            style={{ backgroundColor: COLORS.primary }}
          >
            Grant Access
          </StyledButton>
        </Form>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Section>

      <Section>
        <SectionTitle>
          Current SuperAdmins
          <Badge isSuper={false} style={{ marginLeft: '12px' }}>
            {users.length} {users.length === 1 ? 'user' : 'users'}
          </Badge>
        </SectionTitle>
        
        {users.length === 0 ? (
          <EmptyState>No superAdmin users found</EmptyState>
        ) : (
          <UserList>
            {users.map((userData) => (
              <UserCard key={userData.uid}>
                <UserInfo>
                  <UserEmail>
                    {userData.email}
                    {userData.uid === user.uid && (
                      <Badge isSuper={true}>You</Badge>
                    )}
                    {userData.isInitialAdmin && (
                      <Badge isSuper={true}>Initial Admin</Badge>
                    )}
                  </UserEmail>
                  <UserRole>
                    UID: {userData.uid}
                  </UserRole>
                </UserInfo>
                
                {userData.uid !== user.uid && (
                  <StyledButton
                    onClick={() => handleRevokeAccess(userData.email)}
                    disabled={isLoading}
                    style={{ 
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      fontSize: '12px',
                      padding: '6px 12px'
                    }}
                  >
                    Revoke Access
                  </StyledButton>
                )}
              </UserCard>
            ))}
          </UserList>
        )}
      </Section>

      <ConfirmModal
        show={showConfirmModal}
        title="Confirm Action"
        message={confirmAction?.message}
        onConfirm={handleConfirm}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
      />
    </Container>
  );
};

export default UserManagement;